/**
 * BJCP 2021 beer guidelines — categories 13–22.
 *
 * The British and American ale families: brown beer, Scottish and Irish ales,
 * the dark British stouts, the strong British ales, and the American pale,
 * amber, porter/stout, IPA and strong-ale categories that grew out of them.
 *
 * The numeric vitals (OG, FG, IBU, SRM, ABV) are facts and are reproduced as
 * published. The `overallImpression` prose is OURS — written by us, not BJCP
 * text, and not a sentence-by-sentence paraphrase of it. Entries marked
 * `spec-sheet` carry the published 2021 vitals; entries marked
 * `representative` carry a reconstructed or deliberately broadened envelope
 * (see 21B, which the guidelines publish only per-variant).
 */

import type { Style } from '../../schema/style';

/** BJCP 2021 categories 13–22. @see Style */
export const styleCategories13To22: readonly Style[] = [
  // -------------------------------------------------------------------------
  // 13 — Brown British Beer
  // -------------------------------------------------------------------------
  {
    id: 'bjcp-2021-13a',
    guideline: 'bjcp-2021',
    categoryNumber: 13,
    categoryLetter: 'A',
    name: 'Dark Mild',
    categoryName: 'Brown British Beer',
    vitals: {
      og: { min: 1.03, max: 1.038 },
      fg: { min: 1.008, max: 1.013 },
      ibu: { min: 10, max: 25 },
      srm: { min: 12, max: 25 },
      abv: { min: 3, max: 3.8 },
    },
    overallImpression:
      'A gentle dark ale built for volume drinking, where a modest gravity still has to deliver caramel, toast and a hint of roast. Bitterness is low and the finish is soft, so the malt does all the talking.',
    commercialExamples: [
      'Moorhouse Black Cat',
      "Banks's Mild",
      'Theakston Traditional Mild',
      'Rudgate Ruby Mild',
    ],
    tags: ['ale', 'british', 'english', 'dark', 'malty', 'session', 'low-alcohol'],
    provenance: 'spec-sheet',
  },
  {
    id: 'bjcp-2021-13b',
    guideline: 'bjcp-2021',
    categoryNumber: 13,
    categoryLetter: 'B',
    name: 'British Brown Ale',
    categoryName: 'Brown British Beer',
    vitals: {
      og: { min: 1.04, max: 1.052 },
      fg: { min: 1.008, max: 1.013 },
      ibu: { min: 20, max: 30 },
      srm: { min: 12, max: 22 },
      abv: { min: 4.2, max: 5.4 },
    },
    overallImpression:
      'A malt-forward brown ale with nutty, biscuity and light caramel character and only enough hop bitterness to keep it from cloying. Drier and more assertive than a mild, but never roasty enough to read as a porter.',
    commercialExamples: [
      'Newcastle Brown Ale',
      'Samuel Smith Nut Brown Ale',
      'Riggwelter Yorkshire Ale',
      'Maxim Double Maxim',
    ],
    tags: ['ale', 'british', 'english', 'brown', 'malty', 'nutty'],
    provenance: 'spec-sheet',
  },
  {
    id: 'bjcp-2021-13c',
    guideline: 'bjcp-2021',
    categoryNumber: 13,
    categoryLetter: 'C',
    name: 'English Porter',
    categoryName: 'Brown British Beer',
    vitals: {
      og: { min: 1.04, max: 1.052 },
      fg: { min: 1.008, max: 1.014 },
      ibu: { min: 18, max: 35 },
      srm: { min: 20, max: 30 },
      abv: { min: 4, max: 5.4 },
    },
    overallImpression:
      'A moderate-strength dark ale that leans on chocolate and light coffee rather than the sharp burnt edge of a stout. Restrained English hopping and a medium body keep it drinkable rather than imposing.',
    commercialExamples: [
      "Fuller's London Porter",
      'Samuel Smith Taddy Porter',
      'Burton Bridge Burton Porter',
      'RCH Old Slug Porter',
    ],
    tags: ['ale', 'british', 'english', 'porter', 'dark', 'roasty', 'chocolate'],
    provenance: 'spec-sheet',
  },

  // -------------------------------------------------------------------------
  // 14 — Scottish Ale
  // -------------------------------------------------------------------------
  {
    id: 'bjcp-2021-14a',
    guideline: 'bjcp-2021',
    categoryNumber: 14,
    categoryLetter: 'A',
    name: 'Scottish Light',
    categoryName: 'Scottish Ale',
    vitals: {
      og: { min: 1.03, max: 1.035 },
      fg: { min: 1.01, max: 1.013 },
      ibu: { min: 10, max: 20 },
      srm: { min: 17, max: 22 },
      abv: { min: 2.5, max: 3.2 },
    },
    overallImpression:
      'The smallest of the Scottish ales: low in alcohol, deep amber to brown, and defined by a soft caramel malt sweetness. Hops are a background note and the fermentation character is clean, leaving a gentle, easy pint.',
    commercialExamples: ["McEwan's 60/-", 'Belhaven 60/-', 'Maclay 60/- Light'],
    tags: ['ale', 'scottish', 'british', 'malty', 'session', 'low-alcohol', 'caramel'],
    provenance: 'spec-sheet',
  },
  {
    id: 'bjcp-2021-14b',
    guideline: 'bjcp-2021',
    categoryNumber: 14,
    categoryLetter: 'B',
    name: 'Scottish Heavy',
    categoryName: 'Scottish Ale',
    vitals: {
      og: { min: 1.035, max: 1.04 },
      fg: { min: 1.01, max: 1.015 },
      ibu: { min: 10, max: 20 },
      srm: { min: 13, max: 22 },
      abv: { min: 3.2, max: 3.9 },
    },
    overallImpression:
      'A middleweight Scottish ale with a little more body and malt depth than the light, still centered on caramel and light toast. Bitterness stays low enough that the finish reads faintly sweet rather than crisp.',
    commercialExamples: ['Caledonian 70/-', 'Belhaven 70/-', 'Orkney Raven Ale', "McEwan's 70/-"],
    tags: ['ale', 'scottish', 'british', 'malty', 'session', 'caramel'],
    provenance: 'spec-sheet',
  },
  {
    id: 'bjcp-2021-14c',
    guideline: 'bjcp-2021',
    categoryNumber: 14,
    categoryLetter: 'C',
    name: 'Scottish Export',
    categoryName: 'Scottish Ale',
    vitals: {
      og: { min: 1.04, max: 1.06 },
      fg: { min: 1.01, max: 1.016 },
      ibu: { min: 15, max: 30 },
      srm: { min: 13, max: 22 },
      abv: { min: 3.9, max: 6 },
    },
    overallImpression:
      'The fullest of the three Scottish ales, with a richer caramel-and-toffee malt base and enough bitterness to balance rather than merely support it. Fermentation is cool and clean, so the malt stays in sharp focus.',
    commercialExamples: [
      'Belhaven Scottish Ale',
      'Orkney Dark Island',
      'Caledonian Edinburgh Strong Ale',
      "Broughton Exciseman's 80/-",
    ],
    tags: ['ale', 'scottish', 'british', 'malty', 'caramel', 'toffee'],
    provenance: 'spec-sheet',
  },

  // -------------------------------------------------------------------------
  // 15 — Irish Beer
  // -------------------------------------------------------------------------
  {
    id: 'bjcp-2021-15a',
    guideline: 'bjcp-2021',
    categoryNumber: 15,
    categoryLetter: 'A',
    name: 'Irish Red Ale',
    categoryName: 'Irish Beer',
    vitals: {
      og: { min: 1.036, max: 1.046 },
      fg: { min: 1.01, max: 1.014 },
      ibu: { min: 18, max: 28 },
      srm: { min: 9, max: 14 },
      abv: { min: 3.8, max: 5 },
    },
    overallImpression:
      'An easy-drinking amber ale with light caramel sweetness and a dry, faintly grainy finish that comes from a touch of roasted barley. Balance is close to even, and nothing about it demands attention.',
    commercialExamples: [
      "Smithwick's Irish Ale",
      "O'Hara's Irish Red Ale",
      'Kilkenny Irish Beer',
      'Porterhouse Red Ale',
    ],
    tags: ['ale', 'irish', 'amber', 'malty', 'session', 'caramel'],
    provenance: 'spec-sheet',
  },
  {
    id: 'bjcp-2021-15b',
    guideline: 'bjcp-2021',
    categoryNumber: 15,
    categoryLetter: 'B',
    name: 'Irish Stout',
    categoryName: 'Irish Beer',
    vitals: {
      og: { min: 1.036, max: 1.044 },
      fg: { min: 1.007, max: 1.011 },
      ibu: { min: 25, max: 45 },
      srm: { min: 25, max: 40 },
      abv: { min: 4, max: 4.5 },
    },
    overallImpression:
      'Black, dry and firmly bitter, with coffee-like roast from unmalted roasted barley and a lean body that finishes fast. Low gravity and high drinkability are the whole point.',
    commercialExamples: [
      'Guinness Draught',
      "Murphy's Irish Stout",
      'Beamish Irish Stout',
      "O'Hara's Irish Stout",
    ],
    tags: ['ale', 'irish', 'stout', 'dark', 'roasty', 'dry', 'session'],
    provenance: 'spec-sheet',
  },
  {
    id: 'bjcp-2021-15c',
    guideline: 'bjcp-2021',
    categoryNumber: 15,
    categoryLetter: 'C',
    name: 'Irish Extra Stout',
    categoryName: 'Irish Beer',
    vitals: {
      og: { min: 1.052, max: 1.062 },
      fg: { min: 1.01, max: 1.014 },
      ibu: { min: 35, max: 50 },
      srm: { min: 25, max: 40 },
      abv: { min: 5.5, max: 6.5 },
    },
    overallImpression:
      'A bigger, bottled-strength take on the Irish stout, with more malt depth behind the roast and a fuller body to carry it. Still dry and bitter at the finish, just with more of everything.',
    commercialExamples: [
      'Guinness Extra Stout',
      "O'Hara's Leann Folláin",
      'Sheridan Brothers Extra Stout',
    ],
    tags: ['ale', 'irish', 'stout', 'dark', 'roasty', 'dry'],
    provenance: 'spec-sheet',
  },

  // -------------------------------------------------------------------------
  // 16 — Dark British Beer
  // -------------------------------------------------------------------------
  {
    id: 'bjcp-2021-16a',
    guideline: 'bjcp-2021',
    categoryNumber: 16,
    categoryLetter: 'A',
    name: 'Sweet Stout',
    categoryName: 'Dark British Beer',
    vitals: {
      og: { min: 1.044, max: 1.06 },
      fg: { min: 1.012, max: 1.024 },
      ibu: { min: 20, max: 40 },
      srm: { min: 30, max: 40 },
      abv: { min: 4, max: 6 },
    },
    overallImpression:
      'A full-bodied dark ale sweetened by unfermentable lactose, so the roast reads as coffee-with-cream rather than bitter char. Low apparent attenuation and modest bitterness make it the dessert end of the stout family.',
    commercialExamples: [
      "Mackeson's XXX Stout",
      'Left Hand Milk Stout',
      'Samuel Adams Cream Stout',
      'Bristol Beer Factory Milk Stout',
    ],
    tags: ['ale', 'british', 'english', 'stout', 'dark', 'sweet', 'lactose', 'milk-stout'],
    provenance: 'spec-sheet',
  },
  {
    id: 'bjcp-2021-16b',
    guideline: 'bjcp-2021',
    categoryNumber: 16,
    categoryLetter: 'B',
    name: 'Oatmeal Stout',
    categoryName: 'Dark British Beer',
    vitals: {
      og: { min: 1.045, max: 1.065 },
      fg: { min: 1.01, max: 1.018 },
      ibu: { min: 25, max: 40 },
      srm: { min: 22, max: 40 },
      abv: { min: 4.2, max: 5.9 },
    },
    overallImpression:
      'A dark stout whose defining trait is texture: oats give it a silky, slick mouthfeel that softens the roast without adding much sweetness. Balance sits between the dry Irish stout and the sweet stout.',
    commercialExamples: [
      'Samuel Smith Oatmeal Stout',
      'Anderson Valley Barney Flats Oatmeal Stout',
      'Founders Breakfast Stout',
      "Young's Oatmeal Stout",
    ],
    tags: ['ale', 'british', 'english', 'stout', 'dark', 'roasty', 'oatmeal', 'smooth'],
    provenance: 'spec-sheet',
  },
  {
    id: 'bjcp-2021-16c',
    guideline: 'bjcp-2021',
    categoryNumber: 16,
    categoryLetter: 'C',
    name: 'Tropical Stout',
    categoryName: 'Dark British Beer',
    vitals: {
      og: { min: 1.056, max: 1.075 },
      fg: { min: 1.01, max: 1.018 },
      ibu: { min: 30, max: 50 },
      srm: { min: 30, max: 40 },
      abv: { min: 5.5, max: 8 },
    },
    overallImpression:
      'A strong, distinctly sweet stout brewed for hot climates, often warm-fermented so ripe fruit esters sit alongside the roast. Think of it as a sweet stout scaled up in gravity and turned up in fruitiness.',
    commercialExamples: ['Lion Stout', 'Dragon Stout', 'ABC Extra Stout', 'Jamaica Stout'],
    tags: ['ale', 'stout', 'dark', 'sweet', 'strong', 'fruity', 'tropical'],
    provenance: 'representative',
  },
  {
    id: 'bjcp-2021-16d',
    guideline: 'bjcp-2021',
    categoryNumber: 16,
    categoryLetter: 'D',
    name: 'Foreign Extra Stout',
    categoryName: 'Dark British Beer',
    vitals: {
      og: { min: 1.056, max: 1.075 },
      fg: { min: 1.01, max: 1.018 },
      ibu: { min: 50, max: 70 },
      srm: { min: 30, max: 40 },
      abv: { min: 6.3, max: 8 },
    },
    overallImpression:
      'The export-strength stout: bigger than an Irish extra stout, firmly bitter, and dry in the finish despite its gravity. Roast is intense but clean, without the burnt harshness of an imperial.',
    commercialExamples: [
      'Guinness Foreign Extra Stout',
      'Coopers Best Extra Stout',
      'Ridgeway Foreign Export Stout',
      'Elysian Dragonstooth Stout',
    ],
    tags: ['ale', 'british', 'stout', 'dark', 'roasty', 'bitter', 'strong'],
    provenance: 'spec-sheet',
  },

  // -------------------------------------------------------------------------
  // 17 — Strong British Ale
  // -------------------------------------------------------------------------
  {
    id: 'bjcp-2021-17a',
    guideline: 'bjcp-2021',
    categoryNumber: 17,
    categoryLetter: 'A',
    name: 'British Strong Ale',
    categoryName: 'Strong British Ale',
    vitals: {
      og: { min: 1.055, max: 1.08 },
      fg: { min: 1.015, max: 1.022 },
      ibu: { min: 30, max: 60 },
      srm: { min: 8, max: 22 },
      abv: { min: 5.5, max: 8 },
    },
    overallImpression:
      'A catch-all for strong, malt-driven British ales that are neither aged like an old ale nor as big as a barleywine. Expect rich caramel, toast and dark fruit, with bitterness present but always subordinate to malt.',
    commercialExamples: [
      "Fuller's 1845",
      'Adnams Broadside',
      'Samuel Smith Winter Welcome',
      'Shepherd Neame Bishops Finger',
    ],
    tags: ['ale', 'british', 'english', 'strong', 'malty', 'caramel', 'dark-fruit'],
    provenance: 'representative',
  },
  {
    id: 'bjcp-2021-17b',
    guideline: 'bjcp-2021',
    categoryNumber: 17,
    categoryLetter: 'B',
    name: 'Old Ale',
    categoryName: 'Strong British Ale',
    vitals: {
      og: { min: 1.055, max: 1.088 },
      fg: { min: 1.015, max: 1.022 },
      ibu: { min: 30, max: 60 },
      srm: { min: 10, max: 22 },
      abv: { min: 5.5, max: 9 },
    },
    overallImpression:
      'A strong British ale that has spent real time in the cellar, picking up dried-fruit, sherry and leather notes from oxidation and slow maturation. A mild lactic or Brettanomyces edge is welcome so long as it stays complexity, not fault.',
    commercialExamples: [
      'Theakston Old Peculier',
      "Fuller's Vintage Ale",
      "Gale's Prize Old Ale",
      'Greene King Olde Suffolk',
    ],
    tags: ['ale', 'british', 'english', 'strong', 'aged', 'malty', 'dark-fruit', 'vinous'],
    provenance: 'spec-sheet',
  },
  {
    id: 'bjcp-2021-17c',
    guideline: 'bjcp-2021',
    categoryNumber: 17,
    categoryLetter: 'C',
    name: 'Wee Heavy',
    categoryName: 'Strong British Ale',
    vitals: {
      og: { min: 1.07, max: 1.13 },
      fg: { min: 1.018, max: 1.04 },
      ibu: { min: 17, max: 35 },
      srm: { min: 14, max: 25 },
      abv: { min: 6.5, max: 10 },
    },
    overallImpression:
      'The strong Scottish ale: deeply malty, chewy and low in bitterness, with caramel and toffee often edged by kettle-caramelization flavors. Alcohol is warming but the beer should still finish malty rather than hot.',
    commercialExamples: [
      'Traquair House Ale',
      'Belhaven Wee Heavy',
      'AleSmith Wee Heavy',
      'Founders Dirty Bastard',
    ],
    tags: ['ale', 'scottish', 'british', 'strong', 'malty', 'caramel', 'toffee'],
    provenance: 'spec-sheet',
  },
  {
    id: 'bjcp-2021-17d',
    guideline: 'bjcp-2021',
    categoryNumber: 17,
    categoryLetter: 'D',
    name: 'English Barleywine',
    categoryName: 'Strong British Ale',
    vitals: {
      og: { min: 1.08, max: 1.12 },
      fg: { min: 1.018, max: 1.03 },
      ibu: { min: 35, max: 70 },
      srm: { min: 8, max: 22 },
      abv: { min: 8, max: 12 },
    },
    overallImpression:
      'The largest of the traditional British ales, built on layers of caramel, toffee and dark fruit with a full, sometimes syrupy body. Hops balance rather than lead, and age turns it vinous and sherry-like.',
    commercialExamples: [
      "Thomas Hardy's Ale",
      'J.W. Lees Harvest Ale',
      "Fuller's Golden Pride",
      'Robinsons Old Tom',
    ],
    tags: ['ale', 'british', 'english', 'barleywine', 'strong', 'malty', 'dark-fruit', 'aged'],
    provenance: 'spec-sheet',
  },

  // -------------------------------------------------------------------------
  // 18 — Pale American Ale
  // -------------------------------------------------------------------------
  {
    id: 'bjcp-2021-18a',
    guideline: 'bjcp-2021',
    categoryNumber: 18,
    categoryLetter: 'A',
    name: 'Blonde Ale',
    categoryName: 'Pale American Ale',
    vitals: {
      og: { min: 1.038, max: 1.054 },
      fg: { min: 1.008, max: 1.013 },
      ibu: { min: 15, max: 28 },
      srm: { min: 3, max: 6 },
      abv: { min: 3.8, max: 5.5 },
    },
    overallImpression:
      "The American craft brewery's approachable house pale ale: clean, lightly bready and gently hopped, with nothing sharp enough to put anyone off. Balance leans very slightly malty and the finish is crisp.",
    commercialExamples: [
      'Kona Big Wave Golden Ale',
      'Russian River Aud Blonde',
      'Deschutes Twilight Ale',
      'Victory Summer Love',
    ],
    tags: ['ale', 'american', 'pale', 'blonde', 'balanced', 'session', 'easy-drinking'],
    provenance: 'spec-sheet',
  },
  {
    id: 'bjcp-2021-18b',
    guideline: 'bjcp-2021',
    categoryNumber: 18,
    categoryLetter: 'B',
    name: 'American Pale Ale',
    categoryName: 'Pale American Ale',
    vitals: {
      og: { min: 1.045, max: 1.06 },
      fg: { min: 1.01, max: 1.015 },
      ibu: { min: 30, max: 50 },
      srm: { min: 5, max: 10 },
      abv: { min: 4.5, max: 6.2 },
    },
    overallImpression:
      'A moderate-strength pale ale that puts American hop aroma — citrus, pine, stone fruit, resin — over a clean malt base without tipping into IPA intensity. Bitterness is firm, the finish dry, and drinkability is the design goal.',
    commercialExamples: [
      'Sierra Nevada Pale Ale',
      'Stone Pale Ale 2.0',
      'Deschutes Mirror Pond Pale Ale',
      'Half Acre Daisy Cutter',
      'Firestone Walker Pale 31',
    ],
    tags: ['ale', 'american', 'pale', 'hoppy', 'citrus', 'pine', 'balanced'],
    provenance: 'spec-sheet',
  },

  // -------------------------------------------------------------------------
  // 19 — Amber and Brown American Beer
  // -------------------------------------------------------------------------
  {
    id: 'bjcp-2021-19a',
    guideline: 'bjcp-2021',
    categoryNumber: 19,
    categoryLetter: 'A',
    name: 'American Amber Ale',
    categoryName: 'Amber and Brown American Beer',
    vitals: {
      og: { min: 1.045, max: 1.06 },
      fg: { min: 1.01, max: 1.015 },
      ibu: { min: 25, max: 40 },
      srm: { min: 10, max: 17 },
      abv: { min: 4.5, max: 6.2 },
    },
    overallImpression:
      'Essentially an American pale ale given a caramel-malt backbone, so hop character shares the stage instead of owning it. Medium body, amber color and an even balance that holds up to food.',
    commercialExamples: [
      "Bell's Amber Ale",
      'Tröegs HopBack Amber Ale',
      'Full Sail Amber',
      'Anderson Valley Boont Amber Ale',
    ],
    tags: ['ale', 'american', 'amber', 'hoppy', 'caramel', 'balanced'],
    provenance: 'spec-sheet',
  },
  {
    id: 'bjcp-2021-19b',
    guideline: 'bjcp-2021',
    categoryNumber: 19,
    categoryLetter: 'B',
    name: 'California Common',
    categoryName: 'Amber and Brown American Beer',
    vitals: {
      og: { min: 1.048, max: 1.054 },
      fg: { min: 1.011, max: 1.014 },
      ibu: { min: 30, max: 45 },
      srm: { min: 10, max: 14 },
      abv: { min: 4.5, max: 5.5 },
    },
    overallImpression:
      'A lager yeast fermented warm in shallow vessels, giving a beer that is mostly clean but carries a light fruity lift. Toasty amber malt meets the rustic, minty-woody bite of American Northern Brewer hops.',
    commercialExamples: [
      'Anchor Steam Beer',
      'Steamworks Steam Engine Lager',
      'Southampton Steem Beer',
    ],
    tags: ['american', 'amber', 'hybrid', 'lager-yeast', 'toasty', 'woody', 'historical'],
    provenance: 'spec-sheet',
  },
  {
    id: 'bjcp-2021-19c',
    guideline: 'bjcp-2021',
    categoryNumber: 19,
    categoryLetter: 'C',
    name: 'American Brown Ale',
    categoryName: 'Amber and Brown American Beer',
    vitals: {
      og: { min: 1.045, max: 1.06 },
      fg: { min: 1.01, max: 1.016 },
      ibu: { min: 20, max: 30 },
      srm: { min: 18, max: 35 },
      abv: { min: 4.3, max: 6.2 },
    },
    overallImpression:
      'A brown ale with American hopping and a bigger chocolate-and-caramel malt core than its British cousin. Roast stays light, bitterness is moderate, and the whole thing sits comfortably between a pale ale and a porter.',
    commercialExamples: [
      "Bell's Best Brown Ale",
      'Big Sky Moose Drool Brown Ale',
      'Brooklyn Brown Ale',
      'Smuttynose Old Brown Dog Ale',
    ],
    tags: ['ale', 'american', 'brown', 'malty', 'chocolate', 'caramel', 'nutty'],
    provenance: 'spec-sheet',
  },

  // -------------------------------------------------------------------------
  // 20 — American Porter and Stout
  // -------------------------------------------------------------------------
  {
    id: 'bjcp-2021-20a',
    guideline: 'bjcp-2021',
    categoryNumber: 20,
    categoryLetter: 'A',
    name: 'American Porter',
    categoryName: 'American Porter and Stout',
    vitals: {
      og: { min: 1.05, max: 1.07 },
      fg: { min: 1.012, max: 1.018 },
      ibu: { min: 25, max: 50 },
      srm: { min: 22, max: 40 },
      abv: { min: 4.8, max: 6.5 },
    },
    overallImpression:
      'A stronger, darker and more hop-forward reading of the porter, with chocolate and coffee malt flavors pushed harder than in the English version. Bitterness can be substantial and American hop character is entirely fair game.',
    commercialExamples: [
      'Founders Porter',
      'Deschutes Black Butte Porter',
      'Great Lakes Edmund Fitzgerald Porter',
      'Anchor Porter',
    ],
    tags: ['ale', 'american', 'porter', 'dark', 'roasty', 'chocolate', 'coffee'],
    provenance: 'spec-sheet',
  },
  {
    id: 'bjcp-2021-20b',
    guideline: 'bjcp-2021',
    categoryNumber: 20,
    categoryLetter: 'B',
    name: 'American Stout',
    categoryName: 'American Porter and Stout',
    vitals: {
      og: { min: 1.05, max: 1.075 },
      fg: { min: 1.01, max: 1.022 },
      ibu: { min: 35, max: 75 },
      srm: { min: 30, max: 40 },
      abv: { min: 5, max: 7 },
    },
    overallImpression:
      'A big, black, aggressively roasted stout with citrus and pine hop character layered over espresso and dark chocolate. Bitterness comes from both hops and roasted grain, and the finish is dry and long.',
    commercialExamples: [
      'Sierra Nevada Stout',
      'Rogue Shakespeare Oatmeal Stout',
      'Deschutes Obsidian Stout',
      'North Coast Old No. 38 Stout',
    ],
    tags: ['ale', 'american', 'stout', 'dark', 'roasty', 'hoppy', 'coffee'],
    provenance: 'spec-sheet',
  },
  {
    id: 'bjcp-2021-20c',
    guideline: 'bjcp-2021',
    categoryNumber: 20,
    categoryLetter: 'C',
    name: 'Imperial Stout',
    categoryName: 'American Porter and Stout',
    vitals: {
      og: { min: 1.075, max: 1.115 },
      fg: { min: 1.018, max: 1.03 },
      ibu: { min: 50, max: 90 },
      srm: { min: 30, max: 40 },
      abv: { min: 8, max: 12 },
    },
    overallImpression:
      'An intensely rich black ale where roast, dark chocolate, dried fruit and alcohol all arrive at once over a heavy, viscous body. Modern American versions lean hoppy and roast-bitter; English-inspired ones stay sweeter and more vinous.',
    commercialExamples: [
      'North Coast Old Rasputin',
      "Bell's Expedition Stout",
      'Founders Imperial Stout',
      'Courage Imperial Russian Stout',
      'Great Divide Yeti Imperial Stout',
    ],
    tags: ['ale', 'stout', 'imperial', 'dark', 'strong', 'roasty', 'chocolate', 'aged'],
    provenance: 'spec-sheet',
  },

  // -------------------------------------------------------------------------
  // 21 — IPA
  // -------------------------------------------------------------------------
  {
    id: 'bjcp-2021-21a',
    guideline: 'bjcp-2021',
    categoryNumber: 21,
    categoryLetter: 'A',
    name: 'American IPA',
    categoryName: 'IPA',
    vitals: {
      og: { min: 1.056, max: 1.07 },
      fg: { min: 1.008, max: 1.014 },
      ibu: { min: 40, max: 70 },
      srm: { min: 6, max: 14 },
      abv: { min: 5.5, max: 7.5 },
    },
    overallImpression:
      'The defining modern American hop showcase: intense citrus, pine, tropical and resinous hop aroma and flavor over a lean, clean malt base. Bitterness is firm and the finish dry, so the hops carry all the way through.',
    commercialExamples: [
      'Russian River Blind Pig IPA',
      "Bell's Two Hearted Ale",
      'Stone IPA',
      'Firestone Walker Union Jack IPA',
      'Ballast Point Sculpin IPA',
    ],
    tags: ['ale', 'american', 'ipa', 'west-coast', 'hoppy', 'bitter', 'citrus', 'pine', 'tropical'],
    provenance: 'spec-sheet',
  },
  {
    id: 'bjcp-2021-21b',
    guideline: 'bjcp-2021',
    categoryNumber: 21,
    categoryLetter: 'B',
    name: 'Specialty IPA',
    categoryName: 'IPA',
    vitals: {
      og: { min: 1.046, max: 1.085 },
      fg: { min: 0.998, max: 1.02 },
      ibu: { min: 20, max: 100 },
      srm: { min: 2, max: 40 },
      abv: { min: 5.5, max: 9.5 },
    },
    overallImpression:
      "An umbrella entry, not a single beer: the 2021 guidelines name seven variants — Belgian, Black, Brown, Red, Rye, White and Brut IPA — each keeping an IPA's hop-forward drive while borrowing a base style's malt, grain or yeast. The vitals here are the broad envelope across all seven, so judge and design against the specific variant.",
    commercialExamples: [
      'Deschutes Hop in the Dark',
      "Founders Red's Rye IPA",
      'Chouffe Houblon Dobbelen IPA Tripel',
      'Deschutes Chainbreaker White IPA',
      'Sierra Nevada Ruthless Rye IPA',
    ],
    tags: [
      'ale',
      'ipa',
      'specialty',
      'hoppy',
      'belgian-ipa',
      'black-ipa',
      'brown-ipa',
      'red-ipa',
      'rye-ipa',
      'white-ipa',
      'brut-ipa',
    ],
    provenance: 'representative',
  },
  {
    id: 'bjcp-2021-21c',
    guideline: 'bjcp-2021',
    categoryNumber: 21,
    categoryLetter: 'C',
    name: 'Hazy IPA',
    categoryName: 'IPA',
    vitals: {
      og: { min: 1.06, max: 1.085 },
      fg: { min: 1.011, max: 1.018 },
      ibu: { min: 25, max: 60 },
      srm: { min: 3, max: 7 },
      abv: { min: 6, max: 9 },
    },
    overallImpression:
      "BJCP's 2021 name for what brewers call the juicy, hazy or New England IPA: opaque, soft and pillowy, with huge tropical and stone-fruit hop aroma from late and dry hopping. Bitterness is deliberately restrained and the mouthfeel full, so it drinks fruity rather than sharp.",
    commercialExamples: [
      'The Alchemist Focal Banger',
      'Tree House Julius',
      'Trillium Congress Street IPA',
      'Other Half All Green Everything',
      'Sierra Nevada Hazy Little Thing',
    ],
    tags: ['ale', 'american', 'ipa', 'hazy', 'neipa', 'juicy', 'hoppy', 'tropical', 'soft'],
    provenance: 'representative',
  },

  // -------------------------------------------------------------------------
  // 22 — Strong American Ale
  // -------------------------------------------------------------------------
  {
    id: 'bjcp-2021-22a',
    guideline: 'bjcp-2021',
    categoryNumber: 22,
    categoryLetter: 'A',
    name: 'Double IPA',
    categoryName: 'Strong American Ale',
    vitals: {
      og: { min: 1.065, max: 1.085 },
      fg: { min: 1.008, max: 1.018 },
      ibu: { min: 60, max: 120 },
      srm: { min: 6, max: 14 },
      abv: { min: 7.5, max: 10 },
    },
    overallImpression:
      'An American IPA scaled up in gravity and hop load without losing its edge: bitterness is high, hop flavor and aroma are enormous, and the malt is kept simple so it never gets in the way. Alcohol should be felt as warmth, not tasted as heat.',
    commercialExamples: [
      'Russian River Pliny the Elder',
      "Bell's Hopslam Ale",
      'Firestone Walker Double Jack',
      'Ballast Point Dorado Double IPA',
      'Stone Ruination Double IPA',
    ],
    tags: ['ale', 'american', 'ipa', 'double-ipa', 'imperial', 'hoppy', 'bitter', 'strong'],
    provenance: 'spec-sheet',
  },
  {
    id: 'bjcp-2021-22b',
    guideline: 'bjcp-2021',
    categoryNumber: 22,
    categoryLetter: 'B',
    name: 'American Strong Ale',
    categoryName: 'Strong American Ale',
    vitals: {
      og: { min: 1.062, max: 1.09 },
      fg: { min: 1.014, max: 1.024 },
      ibu: { min: 50, max: 100 },
      srm: { min: 7, max: 19 },
      abv: { min: 6.3, max: 10 },
    },
    overallImpression:
      'A strong, hop-heavy American ale with more caramel and toasty malt depth than a double IPA, and a correspondingly fuller finish. It covers the "big hoppy amber ale" territory that does not fit the IPA or barleywine boxes.',
    commercialExamples: [
      'Stone Arrogant Bastard Ale',
      'Great Lakes Nosferatu',
      'Bear Republic Red Rocket Ale',
      'Port Brewing Shark Attack Double Red Ale',
    ],
    tags: ['ale', 'american', 'strong', 'hoppy', 'bitter', 'caramel', 'amber'],
    provenance: 'spec-sheet',
  },
  {
    id: 'bjcp-2021-22c',
    guideline: 'bjcp-2021',
    categoryNumber: 22,
    categoryLetter: 'C',
    name: 'American Barleywine',
    categoryName: 'Strong American Ale',
    vitals: {
      og: { min: 1.08, max: 1.12 },
      fg: { min: 1.016, max: 1.03 },
      ibu: { min: 50, max: 100 },
      srm: { min: 10, max: 19 },
      abv: { min: 8, max: 12 },
    },
    overallImpression:
      'The American answer to the English barleywine: the same rich caramel and dark-fruit malt weight, but with far more hop bitterness, flavor and aroma. Fresh examples are brash and resinous; cellared ones fade toward sherry and toffee.',
    commercialExamples: [
      'Sierra Nevada Bigfoot',
      'Great Divide Old Ruffian',
      'Anchor Old Foghorn',
      'Rogue Old Crustacean',
      'Avery Hog Heaven Barleywine',
    ],
    tags: ['ale', 'american', 'barleywine', 'strong', 'hoppy', 'malty', 'caramel', 'aged'],
    provenance: 'spec-sheet',
  },
  {
    id: 'bjcp-2021-22d',
    guideline: 'bjcp-2021',
    categoryNumber: 22,
    categoryLetter: 'D',
    name: 'Wheatwine',
    categoryName: 'Strong American Ale',
    vitals: {
      og: { min: 1.08, max: 1.12 },
      fg: { min: 1.016, max: 1.03 },
      ibu: { min: 30, max: 60 },
      srm: { min: 8, max: 15 },
      abv: { min: 8, max: 12 },
    },
    overallImpression:
      'A barleywine-strength ale built on a large proportion of wheat, which gives it a softer, breadier malt profile and a slick, full body. Hops balance rather than dominate, and honey, bread crust and light fruit come forward as it ages.',
    commercialExamples: [
      'Smuttynose Wheat Wine Ale',
      'Rubicon Winter Wheat Wine',
      'Two Brothers Bare Tree Weiss Wine',
    ],
    tags: ['ale', 'american', 'wheat', 'wheatwine', 'strong', 'malty', 'honey', 'aged'],
    provenance: 'representative',
  },
];
